import React, { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import {
  parseEnumFromString,
  parseStringFromEnum,
  TAB_QUERY_STRING,
} from '../../../utils/url-tabs-helper';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TabPanel, { a11yProps } from '../../misc/TabsPanel';
import { useTranslation } from 'react-i18next';
import { AgreementType } from '../../../models/enums/agreements';
import { useAppSelector } from '../../../store';
import { selectIsUserAdminOrHigher } from '../../../store/general/general-slice-selectors';
import { AddIcon } from '@common/utils/icons/icons';
import AgreementsForm from '../../agreements/AgreementsForm';
import AgreementsTable from '../../agreements/AgreementsTable';
import CustomLocalizationsTab from '../../admin/custom-localizations/CustomLocalizationsTab';
import CustomLocalizationType from '../../../models/enums/custom-localization-type';
import CustomLocalizationForm from '../../admin/custom-localizations/CustomLocalizationForm';
import { Theme, useMediaQuery } from '@mui/material';
import Typography from '@mui/material/Typography';
import VersionsTab from '../../versions/VersionsTab';
import BuildOverviewPage from '../../service/mission-stats/BuildOverviewPage';
import NavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';

enum PageTabs {
  UserAgreements,
  OrganizationAgreements,
  CustomLocalizations,
  Versions,
  Stats,
}

const AdministrationPage: React.FC = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [filterParams, setFilterParams] = useSearchParams();
  const pageTab = parseEnumFromString(PageTabs, filterParams.get(TAB_QUERY_STRING));
  const matchesSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const isAdmin = useAppSelector((state) => selectIsUserAdminOrHigher(state));

  const [tabsValue, setTabsValue] = useState(pageTab);
  const [isAgreementsFormOpen, setIsAgreementsFormOpen] = useState(false);
  const [isLocalizationFormOpen, setIsLocalizationFormOpen] = useState(false);
  const [localizationType, setLocalizationType] = useState(CustomLocalizationType.CLEANING_MODE);

  const tabChangeHandler = useCallback(
    (tab: PageTabs) => {
      setFilterParams((prevState) => {
        prevState.set(TAB_QUERY_STRING, parseStringFromEnum(PageTabs, tab));
        return prevState;
      });
      setTabsValue(tab);
    },
    [setFilterParams],
  );

  useEffect(() => {
    if (tabsValue !== pageTab) {
      tabChangeHandler(pageTab);
    }
  }, [pageTab, tabChangeHandler, tabsValue]);

  useEffect(() => {
    if (!isAdmin) {
      navigate(NavigationRoutes.Dashboard);
    }
  }, [isAdmin, navigate]);

  const tabChangeClickHandler = (_event: React.SyntheticEvent, newValue: number) => {
    tabChangeHandler(newValue);
  };

  const openAgreementsFormClickHandler = () => {
    setIsAgreementsFormOpen(true);
  };

  const closeAgreementsFormHandler = () => {
    setIsAgreementsFormOpen(false);
  };

  const openAddLocalizationFormClickHandler = () => {
    setIsLocalizationFormOpen(true);
  };

  const closeLocalizationFormHandler = () => {
    setIsLocalizationFormOpen(false);
  };

  const localizationTypeChangeHandler = (newType: CustomLocalizationType) => {
    setLocalizationType(newType);
  };

  const tabActionButtonClickHandler =
    tabsValue === PageTabs.CustomLocalizations
      ? openAddLocalizationFormClickHandler
      : openAgreementsFormClickHandler;

  const tabActionButtonText =
    tabsValue === PageTabs.CustomLocalizations
      ? t('button-add-localization')
      : tabsValue === PageTabs.Versions
        ? t('button-add-version')
        : t(
            'button-add-agreement-' +
              (tabsValue === PageTabs.OrganizationAgreements
                ? AgreementType.ORGANIZATION
                : AgreementType.USER),
          );

  return (
    <React.Fragment>
      <Stack spacing={2} p={2}>
        <Typography variant="h2" fontSize={24} lineHeight={2}>
          {t('button-administration')}
        </Typography>
        <Stack direction="row" justifyContent="space-between">
          <Tabs
            value={tabsValue}
            onChange={tabChangeClickHandler}
            variant="scrollable"
            scrollButtons={false}
          >
            <Tab label={t('title-user-agreements')} {...a11yProps(PageTabs.UserAgreements)} />
            <Tab
              label={t('title-organization-agreements')}
              {...a11yProps(PageTabs.OrganizationAgreements)}
            />
            <Tab
              label={t('title-custom-localizations')}
              {...a11yProps(PageTabs.CustomLocalizations)}
            />
            <Tab label={t('title-update-versions')} {...a11yProps(PageTabs.Versions)} />
            <Tab label={t('title-stats')} {...a11yProps(PageTabs.Stats)} />
          </Tabs>

          {tabsValue !== PageTabs.Stats && tabsValue !== PageTabs.Versions && (
            <React.Fragment>
              {matchesSmDown ? (
                <IconButton onClick={tabActionButtonClickHandler} color="primary">
                  <AddIcon />
                </IconButton>
              ) : (
                <Button startIcon={<AddIcon />} onClick={tabActionButtonClickHandler}>
                  {tabActionButtonText}
                </Button>
              )}
            </React.Fragment>
          )}
        </Stack>

        <TabPanel value={tabsValue} index={PageTabs.UserAgreements}>
          <AgreementsTable agreementType={AgreementType.USER} />
        </TabPanel>
        <TabPanel value={tabsValue} index={PageTabs.OrganizationAgreements}>
          <AgreementsTable agreementType={AgreementType.ORGANIZATION} />
        </TabPanel>
        <TabPanel value={tabsValue} index={PageTabs.CustomLocalizations}>
          <CustomLocalizationsTab
            localizationType={localizationType}
            onLocalizationChange={localizationTypeChangeHandler}
          />
        </TabPanel>
        <TabPanel value={tabsValue} index={PageTabs.Versions}>
          <VersionsTab />
        </TabPanel>
        <TabPanel value={tabsValue} index={PageTabs.Stats}>
          <BuildOverviewPage />
        </TabPanel>
      </Stack>
      <AgreementsForm
        open={isAgreementsFormOpen}
        agreementType={
          tabsValue === PageTabs.OrganizationAgreements
            ? AgreementType.ORGANIZATION
            : AgreementType.USER
        }
        onClose={closeAgreementsFormHandler}
      />
      <CustomLocalizationForm
        open={isLocalizationFormOpen}
        localizationType={localizationType}
        onClose={closeLocalizationFormHandler}
      />
    </React.Fragment>
  );
};

export default AdministrationPage;
